import { useMemo } from "react";
import { useEstimatePresetStore } from "@tools";
import {
  BORDER_HORIZONTAL,
  BORDER_RIGHT,
  BORDER_LEFT,
  RIGHT,
  FILL,
  COLOR,
  CENTER,
  CENTER_VERTICAL,
  mixColor,
} from "./xlsStyleUtils";
import { useTheme } from "@material-ui/core";

export const useXlsStyles = () => {
  
  const presetStore = useEstimatePresetStore();
  const theme = useTheme();
  
  const { selectedPresetId, selectedPresetData } = presetStore;
  
  const usePresetColors = selectedPresetId !== -1;
  const offColor = "#D9D9D9";
  
  const headerColor = useMemo(() => (
    (usePresetColors && mixColor(selectedPresetData.sectionColor, 1, true))
    || theme.palette.primary.main
  ), [ usePresetColors, selectedPresetData, theme ]);
  
  const headerFontColor = useMemo(() => (
    (usePresetColors && selectedPresetData.fontColor.sectionRow)
    || theme.palette.text.alt
  ), [ usePresetColors, selectedPresetData, theme ]);
  
  const rowBorderColor = useMemo(() => (
    (usePresetColors && selectedPresetData.rowBorderColor)
    || theme.palette.primary.main
  ), [ usePresetColors, selectedPresetData, theme ]);
  
  const level1Fill = useMemo(() => (
    (usePresetColors && selectedPresetData.sectionColor)
    || mixColor(theme.palette.primary.main, 1)
  ), [ usePresetColors, selectedPresetData, theme ]);
  
  const level2Fill = useMemo(() => (
    (usePresetColors && selectedPresetData.activeColor1)
    || mixColor(theme.palette.primary.main, 2)
  ), [ usePresetColors, selectedPresetData, theme ]);
  
  const level3Fill = useMemo(() => (
    (usePresetColors && selectedPresetData.activeColor2)
    || mixColor(theme.palette.primary.main, 3)
  ), [ usePresetColors, selectedPresetData, theme ]);
  
  // ---------------------------------------------------------------------------
  
  const titleStyle = (applyFormatting=true) => {
    const style = { alignment: { vertical: "middle" }, font: { size: 14, } };
    if(applyFormatting) {
      style.font.bold = true;
      style.border = BORDER_HORIZONTAL(rowBorderColor);
    }
    
    return style;
  };
  
  const taskStyle = (applyFormatting=true, level, isTurnedOff=false) => {
    const style = { font: {} };
    
    if(applyFormatting && level <= 3) {
      if(level === 0) {
        style.fill = FILL(level1Fill);
        style.font.color = COLOR(headerFontColor);
        style.font.size = 11;
        style.font.bold = true;
      }
      else if(level === 1)
        style.fill = FILL(level2Fill);
      else
        style.fill = FILL(level3Fill);
    }
    
    
    if (isTurnedOff) {
      style.font.color = COLOR(offColor);
      style.fill = undefined; //FILL("#F3F3F3");
    }
    
    return style;
  };
  
  const taskFont = (applyFormatting = true, level, isTurnedOff = false) => {
    const style = { font: {} };
    
    if (applyFormatting && level === 1)
      style.font.bold = true;
    
    if (isTurnedOff)
      style.font.color = COLOR(offColor);
    
    return style;
  };
  
  const valueCellStyle = (
    applyFormatting = true,
    isSection = false,
    align = undefined,
    isTurnedOff = false
  ) => {
    const style = { alignment: CENTER, font: {}};
    let color = rowBorderColor;
    
    if(isTurnedOff)
      style.font.color = COLOR(offColor);
    
    if(applyFormatting) {
      style.font.bold = isSection;
      style.border = align === "left"
        ? BORDER_LEFT(color)
        : align === "right"
          ? BORDER_RIGHT(color)
          : BORDER_HORIZONTAL(color);
    }
    
    return style;
  };
  
  const totalCellStyle = (applyFormatting=true, right = false, left = false) => {
    const style = {
      alignment: left ? undefined : right ? RIGHT : CENTER
    };
    
    if(applyFormatting)
      style.fill = FILL(level2Fill);
    
    return style;
  };
  
  const priceModifierDividerStyle = (applyFormatting=true) => {
    const style = {};
    
    if(applyFormatting)
      style.fill = FILL(level3Fill);
    
    return style;
  };
  
  const dividerStyle = (applyFormatting=true) => {
    const style = { };
    if(applyFormatting)
      style.fill = FILL(headerColor);
    
    return style;
  };
  
  const proposalHeaderStyle = (applyFormatting=true) => {
    const style = { alignment: CENTER };
    if(applyFormatting) {
      style.fill = FILL(headerColor);
      style.font = {
        size: 12,
        color: COLOR(headerFontColor),
        bold: true,
      };
    }
    
    return style;
  };
  
  const tableHeaderStyle = (applyFormatting=true, center=false, isTurnedOff=false) => {
    const style = { font: {} };
    
    if(center)
      style.alignment = { ...CENTER, wrapText: true };
    else
      style.alignment = { ...CENTER_VERTICAL, wrapText: true };
    
    if(applyFormatting) {
      style.fill = FILL(headerColor);
      style.font.color = COLOR(headerFontColor);
    }
    
    if(isTurnedOff)
      style.font.color = COLOR(offColor);
    
    return style;
  };
  
  const turnedOffValueCellStyle = {
    font: {
      color: COLOR(offColor),
    },
  };
  
  return {
    titleStyle,
    taskStyle,
    taskFont,
    valueCellStyle,
    totalCellStyle,
    priceModifierDividerStyle,
    dividerStyle,
    proposalHeaderStyle,
    tableHeaderStyle,
    turnedOffValueCellStyle,
  }
}